import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Badge,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Switch,
  FormControlLabel,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  Business,
  LocationOn,
  Work,
  TrendingUp,
  Assessment,
  MonetizationOn,
  Psychology,
  AutoAwesome,
  Campaign,
  Analytics,
  Visibility,
  ThumbUp,
  Refresh,
  Facebook,
  Instagram,
  LinkedIn,
  Twitter,
  YouTube,
  CheckCircle,
  Warning,
  Info,
  Star,
  EmojiEvents,
  Rocket,
  Timeline,
  AttachMoney,
  TrendingDown,
  ExpandMore,
  ExpandLess,
  FilterList,
  Search,
  Download,
  Upload,
  Sync,
  Notifications,
  Settings,
  People,
  Send,
  Schedule
} from '@mui/icons-material';

const LeadCapture = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [leadForm, setLeadForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    industry: '',
    location: '',
    source: '',
    interests: [],
    budget: '',
    timeline: '',
    notes: '',
    socialProfiles: {
      linkedin: '',
      twitter: '',
      facebook: '',
      instagram: ''
    }
  });

  const [leads, setLeads] = useState([]);

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showROIDialog, setShowROIDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [autoCaptureEnabled, setAutoCaptureEnabled] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);

  // ROI and Analytics State
  const [roiMetrics, setRoiMetrics] = useState({
    totalLeads: 156,
    totalValue: 485000,
    totalCost: 12500,
    totalROI: 3780,
    conversionRate: 0.23,
    avgLeadValue: 3109,
    avgTimeToConversion: 45
  });

  const [funnelData, setFunnelData] = useState({
    awareness: 1200,
    consideration: 450,
    decision: 156,
    conversion: 36
  });

  // Industry-specific lead capture state
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [customKeywords, setCustomKeywords] = useState([]);
  const [industryKeywords, setIndustryKeywords] = useState([]);
  const [autoDetectedIndustry, setAutoDetectedIndustry] = useState('');
  const [showIndustryDialog, setShowIndustryDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [contactMethod, setContactMethod] = useState('email');
  const [contactMessage, setContactMessage] = useState('');
  const [contactSubject, setContactSubject] = useState('');
  const [contactTemplate, setContactTemplate] = useState('');
  const [sendingContact, setSendingContact] = useState(false);
  const [showIntegrationDialog, setShowIntegrationDialog] = useState(false);
  const [emailConnected, setEmailConnected] = useState(false);
  const [smsConnected, setSmsConnected] = useState(false);
  const [integrationStatus, setIntegrationStatus] = useState({
    email: { connected: false, provider: '', email: '' },
    sms: { connected: false, provider: '', phone: '' }
  });

  // Industry templates with keywords and scoring criteria
  const industryTemplates = {
    'real-estate': {
      name: 'Real Estate',
      keywords: {
        highIntent: ['looking to buy', 'need to sell', 'mortgage rates', 'open house', 'property search', 'home inspection', 'closing costs', 'down payment'],
        mediumIntent: ['real estate market', 'housing prices', 'neighborhood info', 'school district', 'property value', 'investment property'],
        lowIntent: ['beautiful home', 'nice neighborhood', 'dream house', 'real estate tips']
      },
      scoring: {
        highIntentScore: 25,
        mediumIntentScore: 15,
        lowIntentScore: 5,
        commentScore: 20,
        shareScore: 15,
        likeScore: 5
      }
    },
    'healthcare': {
      name: 'Healthcare',
      keywords: {
        highIntent: ['need appointment', 'pain relief', 'emergency care', 'treatment options', 'medical consultation', 'urgent care', 'symptoms', 'diagnosis'],
        mediumIntent: ['health tips', 'wellness', 'preventive care', 'medical advice', 'health insurance'],
        lowIntent: ['healthy lifestyle', 'medical information', 'health awareness']
      },
      scoring: {
        highIntentScore: 25,
        mediumIntentScore: 15,
        lowIntentScore: 5,
        commentScore: 20,
        shareScore: 15,
        likeScore: 5
      }
    },
    'plumbing': {
      name: 'Plumbing',
      keywords: {
        highIntent: ['leak emergency', 'broken pipe', 'water heater', 'drain clogged', 'toilet repair', 'urgent plumbing', 'flooding', 'no hot water'],
        mediumIntent: ['plumbing service', 'drain cleaning', 'pipe repair', 'bathroom renovation', 'kitchen sink'],
        lowIntent: ['plumbing tips', 'home maintenance', 'water conservation']
      },
      scoring: {
        highIntentScore: 30,
        mediumIntentScore: 15,
        lowIntentScore: 5,
        commentScore: 25,
        shareScore: 15,
        likeScore: 5
      }
    },
    'handyman': {
      name: 'Handyman',
      keywords: {
        highIntent: ['urgent repair', 'broken door', 'leaky faucet', 'electrical issue', 'home emergency', 'repair needed', 'broken window'],
        mediumIntent: ['home improvement', 'renovation', 'repair service', 'maintenance work', 'installation'],
        lowIntent: ['DIY tips', 'home projects', 'maintenance advice']
      },
      scoring: {
        highIntentScore: 25,
        mediumIntentScore: 15,
        lowIntentScore: 5,
        commentScore: 20,
        shareScore: 15,
        likeScore: 5
      }
    },
    'fitness': {
      name: 'Fitness',
      keywords: {
        highIntent: ['personal trainer', 'workout plan', 'weight loss', 'fitness goals', 'gym membership', 'training session', 'nutrition plan'],
        mediumIntent: ['exercise tips', 'healthy lifestyle', 'workout routine', 'fitness motivation'],
        lowIntent: ['fitness inspiration', 'health tips', 'workout motivation']
      },
      scoring: {
        highIntentScore: 20,
        mediumIntentScore: 15,
        lowIntentScore: 5,
        commentScore: 15,
        shareScore: 10,
        likeScore: 5
      }
    },
    'attorney': {
      name: 'Attorney',
      keywords: {
        highIntent: ['legal consultation', 'need lawyer', 'legal advice', 'case review', 'legal representation', 'court case', 'legal help'],
        mediumIntent: ['legal questions', 'legal rights', 'legal process', 'law information'],
        lowIntent: ['legal tips', 'law awareness', 'legal education']
      },
      scoring: {
        highIntentScore: 30,
        mediumIntentScore: 15,
        lowIntentScore: 5,
        commentScore: 25,
        shareScore: 15,
        likeScore: 5
      }
    }
  };

  const handleInputChange = (field, value) => {
    setLeadForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

    // Contact templates for different industries
  const contactTemplates = {
    'real-estate': {
      email: {
        subject: 'Quick question about your real estate needs',
        message: 'Hi {{firstName}},\n\nI noticed your interest in real estate and wanted to reach out. I\'m {{clientName}} with {{clientCompany}}, and I specialize in helping people like you with their real estate needs.\n\nBased on your engagement, it looks like you might be interested in {{service}}. I\'d love to schedule a quick call to discuss how I can help you achieve your real estate goals.\n\nWould you be available for a 15-minute conversation this week?\n\nBest regards,\n{{clientName}}\n{{clientCompany}}\n{{clientPhone}}'
      },
      text: {
        message: 'Hi {{firstName}}, this is {{clientName}} from {{clientCompany}}. I noticed your interest in real estate and wanted to see if you\'re looking to buy or sell. Would you be available for a quick call this week?'
      }
    },
    'plumbing': {
      email: {
        subject: 'Quick response to your plumbing needs',
        message: 'Hi {{firstName}},\n\nI\'m {{clientName}} with {{clientCompany}}, and I saw you might be dealing with a plumbing issue. I\'m available to help with emergency repairs and regular maintenance.\n\nI can usually respond within 2 hours for urgent issues. Would you like me to schedule a service call or provide a quote?\n\nBest regards,\n{{clientName}}\n{{clientCompany}}\n{{clientPhone}}'
      },
      text: {
        message: 'Hi {{firstName}}, this is {{clientName}} from {{clientCompany}}. I\'m available for your plumbing needs - emergency repairs or regular service. When would be a good time to call?'
      }
    },
    'healthcare': {
      email: {
        subject: 'Healthcare consultation availability',
        message: 'Hi {{firstName}},\n\nI\'m {{clientName}} with {{clientCompany}}, and I noticed you might be looking for healthcare services. I\'d be happy to discuss your needs and see how I can help.\n\nI have availability for consultations this week. Would you like to schedule a time to talk?\n\nBest regards,\n{{clientName}}\n{{clientCompany}}\n{{clientPhone}}'
      },
      text: {
        message: 'Hi {{firstName}}, this is {{clientName}} from {{clientCompany}}. I\'m available for healthcare consultations. Would you like to schedule a time to discuss your needs?'
      }
    },
    'fitness': {
      email: {
        subject: 'Fitness consultation - let\'s discuss your goals',
        message: 'Hi {{firstName}},\n\nI\'m {{clientName}} with {{clientCompany}}, and I noticed your interest in fitness and health. I\'d love to help you achieve your fitness goals with a personalized plan.\n\nWould you be interested in a free consultation to discuss your fitness objectives?\n\nBest regards,\n{{clientName}}\n{{clientCompany}}\n{{clientPhone}}'
      },
      text: {
        message: 'Hi {{firstName}}, this is {{clientName}} from {{clientCompany}}. I\'d love to help you with your fitness goals. Would you be interested in a free consultation?'
      }
    },
    'attorney': {
      email: {
        subject: 'Legal consultation - I can help with your case',
        message: 'Hi {{firstName}},\n\nI\'m {{clientName}} with {{clientCompany}}, and I noticed you might need legal assistance. I specialize in helping clients with their legal matters and would be happy to discuss your situation.\n\nI offer free initial consultations. Would you like to schedule a time to discuss your case?\n\nBest regards,\n{{clientName}}\n{{clientCompany}}\n{{clientPhone}}'
      },
      text: {
        message: 'Hi {{firstName}}, this is {{clientName}} from {{clientCompany}}. I\'d be happy to discuss your legal needs. Would you like to schedule a free consultation?'
      }
    }
  };

  const handleContactLead = (lead) => {
    setSelectedLead(lead);
    setShowContactDialog(true);
    
    // Auto-populate with industry-specific template
    const template = contactTemplates[selectedIndustry] || contactTemplates['real-estate'];
    setContactSubject(template.email.subject);
    setContactMessage(template.email.message);
    setContactTemplate('email');
  };

  const sendContact = async () => {
    // Check if required integration is connected
    if (contactMethod === 'email' && !integrationStatus.email.connected) {
      alert('Please connect your email service first. Click "Setup Integrations" to connect.');
      setShowContactDialog(false);
      setShowIntegrationDialog(true);
      return;
    }
    
    if (contactMethod === 'text' && !integrationStatus.sms.connected) {
      alert('Please connect your SMS service first. Click "Setup Integrations" to connect.');
      setShowContactDialog(false);
      setShowIntegrationDialog(true);
      return;
    }

    setSendingContact(true);
    try {
      // Simulate sending contact
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Update lead status
      const updatedLeads = leads.map(lead => 
        lead.id === selectedLead.id 
          ? { ...lead, status: 'Contacted', lastContact: new Date().toISOString().split('T')[0] }
          : lead
      );
      setLeads(updatedLeads);
      
      setShowContactDialog(false);
      setSelectedLead(null);
      setContactMessage('');
      setContactSubject('');
    } catch (error) {
      console.error('Error sending contact:', error);
    } finally {
      setSendingContact(false);
    }
  };

  const replaceTemplateVariables = (template, lead) => {
    return template
      .replace(/{{firstName}}/g, lead.firstName)
      .replace(/{{clientName}}/g, 'John Smith') // This would be the actual client name
      .replace(/{{clientCompany}}/g, 'Your Business Name')
      .replace(/{{clientPhone}}/g, '+1 (555) 123-4567')
      .replace(/{{service}}/g, 'real estate services');
  };

  const connectEmailService = async (provider) => {
    // Simulate OAuth flow for email providers
    setIntegrationStatus(prev => ({
      ...prev,
      email: { connected: true, provider, email: 'client@business.com' }
    }));
    setEmailConnected(true);
  };

  const connectSMSService = async (provider) => {
    // Simulate SMS service connection
    setIntegrationStatus(prev => ({
      ...prev,
      sms: { connected: true, provider, phone: '+1 (555) 123-4567' }
    }));
    setSmsConnected(true);
  };

  const disconnectService = (service) => {
    setIntegrationStatus(prev => ({
      ...prev,
      [service]: { connected: false, provider: '', [service === 'email' ? 'email' : 'phone']: '' }
    }));
    if (service === 'email') setEmailConnected(false);
    if (service === 'sms') setSmsConnected(false);
  };

  const calculateLeadScore = (lead) => {
    let score = 0;
    let qualificationReasons = [];
    
    // Company size and position (Decision-making power)
    if (lead.position?.toLowerCase().includes('director') || lead.position?.toLowerCase().includes('ceo') || lead.position?.toLowerCase().includes('vp')) {
      score += 25;
      qualificationReasons.push('Decision-maker (Director/CEO/VP)');
    }
    if (lead.position?.toLowerCase().includes('manager')) {
      score += 15;
      qualificationReasons.push('Management position');
    }
    
    // Industry scoring (High-value industries)
    const highValueIndustries = ['technology', 'finance', 'healthcare', 'marketing', 'consulting'];
    if (highValueIndustries.includes(lead.industry?.toLowerCase())) {
      score += 20;
      qualificationReasons.push('High-value industry');
    }
    
    // Company size scoring
    if (lead.companySize) {
      if (lead.companySize.includes('50-200') || lead.companySize.includes('200+')) {
        score += 20;
        qualificationReasons.push('Mid to large company');
      } else if (lead.companySize.includes('10-50')) {
        score += 15;
        qualificationReasons.push('Growing company');
      }
    }
    
    // Budget consideration
    if (lead.budget) {
      const budget = parseInt(lead.budget.replace(/[^0-9]/g, ''));
      if (budget > 25000) {
        score += 30;
        qualificationReasons.push('High budget potential');
      } else if (budget > 10000) {
        score += 20;
        qualificationReasons.push('Good budget range');
      } else if (budget > 5000) {
        score += 10;
        qualificationReasons.push('Moderate budget');
      }
    }
    
    // Engagement score (Social media activity)
    if (lead.engagementScore > 80) {
      score += 25;
      qualificationReasons.push('High social engagement');
    } else if (lead.engagementScore > 60) {
      score += 15;
      qualificationReasons.push('Good social engagement');
    }
    
    // Interest alignment (Topics they engage with)
    const relevantInterests = ['social media', 'marketing', 'lead generation', 'roi', 'analytics', 'growth'];
    const leadInterests = lead.interests?.map(i => i.toLowerCase()) || [];
    const matchingInterests = leadInterests.filter(interest => 
      relevantInterests.some(relevant => interest.includes(relevant))
    );
    
    if (matchingInterests.length >= 2) {
      score += 20;
      qualificationReasons.push('Strong interest alignment');
    } else if (matchingInterests.length >= 1) {
      score += 10;
      qualificationReasons.push('Some interest alignment');
    }
    
    // Timeline urgency
    if (lead.timeline?.toLowerCase().includes('immediate') || lead.timeline?.toLowerCase().includes('1-3')) {
      score += 15;
      qualificationReasons.push('Urgent timeline');
    }
    
    // Location scoring (Target markets)
    const targetLocations = ['san francisco', 'new york', 'austin', 'los angeles', 'chicago', 'boston'];
    if (targetLocations.includes(lead.location?.toLowerCase())) {
      score += 10;
      qualificationReasons.push('Target market location');
    }
    
    return {
      score: Math.min(score, 100),
      reasons: qualificationReasons
    };
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const engagementScore = Math.floor(Math.random() * 40) + 60;
      const leadScore = calculateLeadScore(leadForm);
      
      const newLead = {
        id: leads.length + 1,
        ...leadForm,
        score: leadScore.score,
        status: leadScore.score >= 80 ? 'Hot' : leadScore.score >= 60 ? 'Warm' : 'Cold',
        lastContact: new Date().toISOString().split('T')[0],
        value: Math.floor(Math.random() * 20000) + 5000,
        conversionProbability: (leadScore.score / 100) * 0.8,
        engagementScore,
        socialActivity: {
          posts: Math.floor(Math.random() * 50) + 20,
          engagement: Math.floor(Math.random() * 300) + 100,
          reach: Math.floor(Math.random() * 15000) + 5000
        },
        roi: {
          cost: Math.floor(Math.random() * 1000) + 300,
          revenue: Math.floor(Math.random() * 20000) + 5000,
          roi: 0
        }
      };

      // Calculate ROI
      newLead.roi.roi = ((newLead.roi.revenue - newLead.roi.cost) / newLead.roi.cost) * 100;

      setLeads(prev => [newLead, ...prev]);
      setLeadForm({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        position: '',
        industry: '',
        location: '',
        source: '',
        interests: [],
        budget: '',
        timeline: '',
        notes: '',
        socialProfiles: {
          linkedin: '',
          twitter: '',
          facebook: '',
          instagram: ''
        }
      });
      setShowAddDialog(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error adding lead:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Hot': return 'error';
      case 'Warm': return 'warning';
      case 'Cold': return 'default';
      default: return 'primary';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 70) return 'warning';
    return 'error';
  };

  const getROIColor = (roi) => {
    if (roi >= 1000) return 'success';
    if (roi >= 500) return 'warning';
    return 'error';
  };

  const handleAutoCapture = async () => {
    setLoading(true);
    try {
      // Simulate capturing leads from social media
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const newAutoLeads = [
        {
          id: leads.length + 1,
          firstName: 'Alex',
          lastName: 'Rodriguez',
          email: 'alex.r@innovatech.com',
          phone: '+1 (555) 234-5678',
          company: 'Innovatech Solutions',
          position: 'VP Marketing',
          industry: 'Technology',
          location: 'San Francisco, CA',
          source: 'LinkedIn Auto',
          captureMethod: 'LinkedIn Engagement',
          captureDetails: 'Engaged with 3 posts about social media marketing',
          qualificationCriteria: [
            'Decision-maker (VP Marketing)',
            'High-value industry (Technology)',
            'Mid to large company (50-200 employees)',
            'High social engagement (95/100)',
            'Strong interest alignment (3 matching topics)',
            'Target market location (San Francisco)',
            'Good budget range ($25K-$50K)',
            'Urgent timeline (1-3 months)'
          ],
          captureTriggers: [
            'Liked our post about "Social Media ROI Tracking"',
            'Commented on our "Lead Generation Strategies" content',
            'Shared our "Marketing Analytics" educational post',
            'Followed our company page after engagement',
            'Viewed multiple posts about social media tools'
          ],
          score: 88,
          status: 'Hot',
          lastContact: new Date().toISOString().split('T')[0],
          value: 35000,
          conversionProbability: 0.82,
          engagementScore: 95,
          socialActivity: {
            posts: 67,
            engagement: 445,
            reach: 18000
          },
          roi: {
            cost: 1200,
            revenue: 35000,
            roi: 2817
          },
          contactInfo: {
            linkedin: 'https://linkedin.com/in/alexrodriguez',
            twitter: '@alexrodriguez',
            companyWebsite: 'https://innovatech.com',
            companySize: '50-200 employees',
            budget: '$25K-$50K',
            timeline: '1-3 months'
          },
          interests: ['Social Media Marketing', 'Lead Generation', 'ROI Tracking'],
          notes: 'Shows strong interest in social media ROI. Engaged with content about lead generation and conversion tracking.'
        },
        {
          id: leads.length + 2,
          firstName: 'Sarah',
          lastName: 'Chen',
          email: 'sarah.chen@digitalflow.com',
          phone: '+1 (555) 345-6789',
          company: 'DigitalFlow Agency',
          position: 'Marketing Director',
          industry: 'Marketing',
          location: 'New York, NY',
          source: 'Instagram Auto',
          captureMethod: 'Instagram Story Engagement',
          captureDetails: 'Interacted with Instagram story about social media tools',
          qualificationCriteria: [
            'Decision-maker (Marketing Director)',
            'High-value industry (Marketing)',
            'Growing company (10-50 employees)',
            'Good social engagement (78/100)',
            'Some interest alignment (2 matching topics)',
            'Target market location (New York)',
            'Moderate budget range ($10K-$25K)',
            'Standard timeline (2-4 months)'
          ],
          captureTriggers: [
            'Viewed our Instagram story about "Social Media Analytics"',
            'Liked our post about "Content Creation Tools"',
            'Commented on our "Instagram Marketing Tips" content',
            'Saved our post about "Marketing ROI"',
            'Followed our account after story interaction'
          ],
          score: 76,
          status: 'Warm',
          lastContact: new Date().toISOString().split('T')[0],
          value: 22000,
          conversionProbability: 0.65,
          engagementScore: 78,
          socialActivity: {
            posts: 45,
            engagement: 234,
            reach: 12000
          },
          roi: {
            cost: 800,
            revenue: 22000,
            roi: 2650
          },
          contactInfo: {
            linkedin: 'https://linkedin.com/in/sarahchen',
            instagram: '@sarahchen_marketing',
            companyWebsite: 'https://digitalflow.com',
            companySize: '10-50 employees',
            budget: '$10K-$25K',
            timeline: '2-4 months'
          },
          interests: ['Content Creation', 'Instagram Marketing', 'Analytics'],
          notes: 'Active on Instagram. Looking for tools to improve social media performance and analytics.'
        },
        {
          id: leads.length + 3,
          firstName: 'Michael',
          lastName: 'Thompson',
          email: 'michael.t@techstartup.io',
          phone: '+1 (555) 456-7890',
          company: 'TechStartup.io',
          position: 'Founder & CEO',
          industry: 'Technology',
          location: 'Austin, TX',
          source: 'Facebook Auto',
          captureMethod: 'Facebook Page Interaction',
          captureDetails: 'Liked and commented on Facebook post about business growth',
          qualificationCriteria: [
            'Decision-maker (Founder & CEO)',
            'High-value industry (Technology)',
            'Growing company (5-20 employees)',
            'High social engagement (98/100)',
            'Strong interest alignment (3 matching topics)',
            'Target market location (Austin)',
            'High budget potential ($50K+)',
            'Urgent timeline (Immediate)'
          ],
          captureTriggers: [
            'Liked our post about "Business Growth Strategies"',
            'Commented on our "Startup Marketing ROI" content',
            'Shared our "Social Media for Startups" educational post',
            'Reacted to multiple business growth posts',
            'Messaged our page about "Marketing Solutions"'
          ],
          score: 92,
          status: 'Hot',
          lastContact: new Date().toISOString().split('T')[0],
          value: 45000,
          conversionProbability: 0.88,
          engagementScore: 98,
          socialActivity: {
            posts: 89,
            engagement: 567,
            reach: 25000
          },
          roi: {
            cost: 1500,
            revenue: 45000,
            roi: 2900
          },
          contactInfo: {
            linkedin: 'https://linkedin.com/in/michaelthompson',
            facebook: 'https://facebook.com/michael.thompson',
            companyWebsite: 'https://techstartup.io',
            companySize: '5-20 employees',
            budget: '$50K+',
            timeline: 'Immediate'
          },
          interests: ['Business Growth', 'Startup Marketing', 'Social Media ROI'],
          notes: 'High-value startup founder. Very engaged with growth content. Ready to invest in proven solutions.'
        }
      ];

      setLeads(prev => [...newAutoLeads, ...prev]);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Auto capture error:', error);
    } finally {
      setLoading(false);
    }
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index} style={{ paddingTop: '20px' }}>
      {value === index && children}
    </div>
  );

  return (
    <Box sx={{ p: { xs: 0.25, sm: 1 }, pb: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person color="primary" />
          Lead Generation & ROI Tracking
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={autoCaptureEnabled}
                onChange={(e) => setAutoCaptureEnabled(e.target.checked)}
              />
            }
            label="Auto Capture"
          />
          <Button
            variant="outlined"
            startIcon={<Sync />}
            onClick={handleAutoCapture}
            disabled={loading || !autoCaptureEnabled}
          >
            {loading ? 'Capturing...' : 'Capture Leads'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Person />}
            onClick={() => setShowAddDialog(true)}
          >
            Add Lead
          </Button>
        </Box>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Lead captured successfully! ROI tracking enabled.
        </Alert>
      )}

      {/* ROI Metrics Overview */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
            <CardContent>
              <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MonetizationOn />
                ${roiMetrics.totalROI.toLocaleString()}%
              </Typography>
              <Typography variant="body2">Total ROI</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: 'success.main', color: 'white' }}>
            <CardContent>
              <Typography variant="h4">
                ${roiMetrics.totalValue.toLocaleString()}
              </Typography>
              <Typography variant="body2">Total Lead Value</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: 'warning.main', color: 'white' }}>
            <CardContent>
              <Typography variant="h4">
                {roiMetrics.conversionRate * 100}%
              </Typography>
              <Typography variant="body2">Conversion Rate</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: 'info.main', color: 'white' }}>
            <CardContent>
              <Typography variant="h4">
                {roiMetrics.avgTimeToConversion} days
              </Typography>
              <Typography variant="body2">Avg Time to Convert</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs for different views */}
      <Card sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label="Lead Management" />
            <Tab label="ROI Analytics" />
            <Tab label="Conversion Funnel" />
            <Tab label="Social Capture" />
            <Tab label="Client Lead Capture" />
            <Tab label="Industry Setup" />
            <Tab label="How It Works" />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            {/* Lead Statistics */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Assessment color="primary" />
                    Lead Statistics
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h4" color="primary">
                      {leads.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Leads
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" color="success.main">
                      {leads.filter(l => l.status === 'Hot').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Hot Leads
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" color="warning.main">
                      {leads.filter(l => l.status === 'Warm').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Warm Leads
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Lead Sources Performance */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TrendingUp color="primary" />
                    Lead Sources Performance
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="primary">
                          {leads.filter(l => l.source === 'LinkedIn').length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          LinkedIn
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="primary">
                          {leads.filter(l => l.source === 'Instagram').length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Instagram
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="primary">
                          {leads.filter(l => l.source === 'Facebook').length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Facebook
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="primary">
                          {leads.filter(l => l.source === 'Website').length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Website
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Lead List */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Recent Leads with ROI Tracking
                  </Typography>
                  <Grid container spacing={2}>
                    {leads.map((lead) => (
                      <Grid item xs={12} md={6} lg={4} key={lead.id}>
                        <Card variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => setSelectedLead(lead)}>
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {lead.firstName} {lead.lastName}
                              </Typography>
                              <Chip 
                                label={lead.status} 
                                color={getStatusColor(lead.status)} 
                                size="small" 
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {lead.position} at {lead.company}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {lead.email}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Chip 
                                label={`Score: ${lead.score}`} 
                                color={getScoreColor(lead.score)} 
                                size="small" 
                              />
                              <Typography variant="caption" color="text.secondary">
                                {lead.source}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Chip 
                                label={`ROI: ${lead.roi.roi}%`} 
                                color={getROIColor(lead.roi.roi)} 
                                size="small" 
                              />
                              <Typography variant="caption" color="text.secondary">
                                ${lead.value.toLocaleString()}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    ROI Performance by Lead
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Lead</TableCell>
                          <TableCell>Value</TableCell>
                          <TableCell>Cost</TableCell>
                          <TableCell>ROI %</TableCell>
                          <TableCell>Probability</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {leads.map((lead) => (
                          <TableRow key={lead.id}>
                            <TableCell>
                              <Box>
                                <Typography variant="subtitle2">
                                  {lead.firstName} {lead.lastName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {lead.company}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>${lead.value.toLocaleString()}</TableCell>
                            <TableCell>${lead.roi.cost.toLocaleString()}</TableCell>
                            <TableCell>
                              <Chip 
                                label={`${lead.roi.roi}%`} 
                                color={getROIColor(lead.roi.roi)} 
                                size="small" 
                              />
                            </TableCell>
                            <TableCell>
                              <LinearProgress 
                                variant="determinate" 
                                value={lead.conversionProbability * 100} 
                                sx={{ width: 60, height: 8 }}
                              />
                              <Typography variant="caption">
                                {(lead.conversionProbability * 100).toFixed(0)}%
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    ROI Summary
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <MonetizationOn color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Total Revenue"
                        secondary={`$${leads.reduce((sum, lead) => sum + lead.value, 0).toLocaleString()}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <AttachMoney color="warning" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Total Cost"
                        secondary={`$${leads.reduce((sum, lead) => sum + lead.roi.cost, 0).toLocaleString()}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <TrendingUp color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Average ROI"
                        secondary={`${(leads.reduce((sum, lead) => sum + lead.roi.roi, 0) / leads.length).toFixed(1)}%`}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Conversion Funnel
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Awareness</Typography>
                      <Typography variant="body2">{funnelData.awareness}</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={100} sx={{ height: 8, mb: 2 }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Consideration</Typography>
                      <Typography variant="body2">{funnelData.consideration}</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={75} sx={{ height: 8, mb: 2 }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Decision</Typography>
                      <Typography variant="body2">{funnelData.decision}</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={50} sx={{ height: 8, mb: 2 }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Conversion</Typography>
                      <Typography variant="body2">{funnelData.conversion}</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={25} sx={{ height: 8 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Funnel Metrics
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <Visibility color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Awareness to Consideration"
                        secondary={`${((funnelData.consideration / funnelData.awareness) * 100).toFixed(1)}%`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Psychology color="warning" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Consideration to Decision"
                        secondary={`${((funnelData.decision / funnelData.consideration) * 100).toFixed(1)}%`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Decision to Conversion"
                        secondary={`${((funnelData.conversion / funnelData.decision) * 100).toFixed(1)}%`}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Social Media Lead Capture
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <LinkedIn color="primary" />
                            <Typography variant="h6">LinkedIn</Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Capturing leads from LinkedIn engagement and connections
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Leads Captured</Typography>
                            <Typography variant="body2" fontWeight="bold">12</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Avg Value</Typography>
                            <Typography variant="body2" fontWeight="bold">$28,500</Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Instagram color="primary" />
                            <Typography variant="h6">Instagram</Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Automated lead capture from Instagram engagement
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Leads Captured</Typography>
                            <Typography variant="body2" fontWeight="bold">8</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Avg Value</Typography>
                            <Typography variant="body2" fontWeight="bold">$15,200</Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Facebook color="primary" />
                            <Typography variant="h6">Facebook</Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Lead generation from Facebook page interactions
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Leads Captured</Typography>
                            <Typography variant="body2" fontWeight="bold">6</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Avg Value</Typography>
                            <Typography variant="body2" fontWeight="bold">$12,800</Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Twitter color="primary" />
                            <Typography variant="h6">Twitter</Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Lead capture from Twitter conversations and mentions
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Leads Captured</Typography>
                            <Typography variant="body2" fontWeight="bold">4</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Avg Value</Typography>
                            <Typography variant="body2" fontWeight="bold">$9,500</Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Auto Capture Settings
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <AutoAwesome color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="AI Lead Scoring"
                        secondary="Automatically score leads based on engagement"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Notifications color="warning" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Real-time Alerts"
                        secondary="Get notified of high-value leads"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Sync color="info" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Auto Sync"
                        secondary="Sync leads across all platforms"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h5" gutterBottom color="primary">
                🎯 Client Lead Capture from Their Social Media
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Monitor your clients' social media posts and automatically capture leads from people who engage with their content.
              </Typography>
            </Grid>

            {/* Client Lead Capture Overview */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Active Client Campaigns
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          <Business color="primary" sx={{ mr: 1 }} />
                          TechStartup.io
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          LinkedIn, Twitter, Instagram
                        </Typography>
                        <Typography variant="h6" color="success.main" sx={{ mt: 1 }}>
                          23 leads captured this week
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={75} 
                          sx={{ mt: 1 }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          <Business color="primary" sx={{ mr: 1 }} />
                          DigitalFlow Agency
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Facebook, LinkedIn, YouTube
                        </Typography>
                        <Typography variant="h6" color="success.main" sx={{ mt: 1 }}>
                          18 leads captured this week
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={60} 
                          sx={{ mt: 1 }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Client Lead Capture Stats */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Capture Performance
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">
                          156
                        </Typography>
                        <Typography variant="body2">
                          Total Leads Captured
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main">
                          89%
                        </Typography>
                        <Typography variant="body2">
                          Capture Rate
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="info.main">
                          12
                        </Typography>
                        <Typography variant="body2">
                          Active Clients
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="warning.main">
                          2.3k
                        </Typography>
                        <Typography variant="body2">
                          Posts Monitored
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Recent Client Leads */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Recent Client Leads Captured
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Lead</TableCell>
                          <TableCell>Client</TableCell>
                          <TableCell>Source Post</TableCell>
                          <TableCell>Engagement</TableCell>
                          <TableCell>Score</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2">Jennifer Martinez</Typography>
                              <Typography variant="body2" color="text.secondary">
                                Marketing Director @ TechCorp
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>TechStartup.io</TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              "How we increased our social media ROI by 300%"
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label="Commented" color="primary" size="small" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="h6" color="success.main">92</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label="Hot" color="success" size="small" />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2">David Chen</Typography>
                              <Typography variant="body2" color="text.secondary">
                                CEO @ GrowthLabs
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>DigitalFlow Agency</TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              "5 strategies for better lead generation"
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label="Shared" color="secondary" size="small" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="h6" color="success.main">88</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label="Warm" color="warning" size="small" />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2">Sarah Williams</Typography>
                              <Typography variant="body2" color="text.secondary">
                                VP Sales @ InnovateTech
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>TechStartup.io</TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              "The future of social media marketing"
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label="Liked" color="info" size="small" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="h6" color="success.main">85</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label="Warm" color="warning" size="small" />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Capture Settings */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Capture Settings
                  </Typography>
                  <FormControl component="fieldset" sx={{ width: '100%' }}>
                    <FormGroup>
                      <FormControlLabel
                        control={<Switch checked={true} />}
                        label="Monitor client posts automatically"
                      />
                      <FormControlLabel
                        control={<Switch checked={true} />}
                        label="Capture leads from comments"
                      />
                      <FormControlLabel
                        control={<Switch checked={true} />}
                        label="Capture leads from shares"
                      />
                      <FormControlLabel
                        control={<Switch checked={false} />}
                        label="Capture leads from likes only"
                      />
                    </FormGroup>
                  </FormControl>
                </CardContent>
              </Card>
            </Grid>

            {/* Capture Triggers */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Capture Triggers
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                      <ListItemText 
                        primary="Comments on client posts" 
                        secondary="Engagement with specific content"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                      <ListItemText 
                        primary="Shares of client content" 
                        secondary="Viral distribution tracking"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                      <ListItemText 
                        primary="Profile visits after engagement" 
                        secondary="Interest in client brand"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                      <ListItemText 
                        primary="Multiple interactions" 
                        secondary="High engagement scoring"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    How Lead Capture Works
                  </Typography>
                  
                  <Typography variant="h6" gutterBottom color="primary">
                    🔍 Automated Lead Detection
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Our AI system monitors social media platforms for users who engage with our educational content about social media marketing:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><LinkedIn color="primary" /></ListItemIcon>
                      <ListItemText 
                        primary="LinkedIn Engagement" 
                        secondary="Likes, comments, shares on our posts about social media ROI and lead generation" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Instagram color="primary" /></ListItemIcon>
                      <ListItemText 
                        primary="Instagram Interactions" 
                        secondary="Story views, post likes, comments on our social media marketing content" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Facebook color="primary" /></ListItemIcon>
                      <ListItemText 
                        primary="Facebook Page Activity" 
                        secondary="Page likes, post engagement, comments on our business growth content" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Twitter color="primary" /></ListItemIcon>
                      <ListItemText 
                        primary="Twitter Engagement" 
                        secondary="Retweets, likes, replies to our social media marketing posts" 
                      />
                    </ListItem>
                  </List>

                  <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                    📝 How It Works: Content Marketing Funnel
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    We create valuable educational content about social media marketing, and capture leads from people who engage with it:
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>Our Content Strategy:</Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon><Campaign /></ListItemIcon>
                          <ListItemText 
                            primary="Educational Posts" 
                            secondary="Tips about social media ROI, lead generation, analytics" 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><Analytics /></ListItemIcon>
                          <ListItemText 
                            primary="Case Studies" 
                            secondary="Real examples of social media success stories" 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><TrendingUp /></ListItemIcon>
                          <ListItemText 
                            primary="Industry Insights" 
                            secondary="Latest trends in social media marketing" 
                          />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>Lead Capture Process:</Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon><Person /></ListItemIcon>
                          <ListItemText 
                            primary="People engage with our content" 
                            secondary="Likes, comments, shares on educational posts" 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><Business /></ListItemIcon>
                          <ListItemText 
                            primary="AI identifies qualified prospects" 
                            secondary="Based on position, company, engagement patterns" 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><MonetizationOn /></ListItemIcon>
                          <ListItemText 
                            primary="Leads provided to clients" 
                            secondary="Ready-to-contact prospects with full details" 
                          />
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>

                  <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                    🎯 Our Content vs. Client Content
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    We create educational content about social media marketing that attracts potential clients:
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom color="success.main">Our Educational Content:</Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                          <ListItemText 
                            primary="Social Media ROI Tips" 
                            secondary="How to measure and improve social media returns" 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                          <ListItemText 
                            primary="Lead Generation Strategies" 
                            secondary="Best practices for capturing leads from social media" 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                          <ListItemText 
                            primary="Analytics & Reporting" 
                            secondary="How to track and optimize social media performance" 
                          />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom color="info.main">Client Content (Separate):</Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon><Business color="info" /></ListItemIcon>
                          <ListItemText 
                            primary="Their Business Posts" 
                            secondary="Client's own social media content and campaigns" 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><Campaign color="info" /></ListItemIcon>
                          <ListItemText 
                            primary="Brand Marketing" 
                            secondary="Client's product/service promotion and brand building" 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><TrendingUp color="info" /></ListItemIcon>
                          <ListItemText 
                            primary="Engagement Management" 
                            secondary="We help manage and optimize their social media presence" 
                          />
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>

                  <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                    📞 Contact Information Provided
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    For each qualified lead, we provide:
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>Basic Contact:</Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon><Person /></ListItemIcon>
                          <ListItemText primary="Full Name" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><Email /></ListItemIcon>
                          <ListItemText primary="Email Address" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><Phone /></ListItemIcon>
                          <ListItemText primary="Phone Number" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><Business /></ListItemIcon>
                          <ListItemText primary="Company & Position" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><LocationOn /></ListItemIcon>
                          <ListItemText primary="Location" />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>Social Profiles:</Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon><LinkedIn /></ListItemIcon>
                          <ListItemText primary="LinkedIn Profile" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><Twitter /></ListItemIcon>
                          <ListItemText primary="Twitter Handle" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><Instagram /></ListItemIcon>
                          <ListItemText primary="Instagram Profile" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><Facebook /></ListItemIcon>
                          <ListItemText primary="Facebook Profile" />
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>

                  <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                    💼 Business Intelligence
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Additional insights to help you close deals:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><People /></ListItemIcon>
                      <ListItemText 
                        primary="Company Size" 
                        secondary="Employee count and growth stage" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><MonetizationOn /></ListItemIcon>
                      <ListItemText 
                        primary="Budget Range" 
                        secondary="Estimated budget based on company size and engagement" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Schedule /></ListItemIcon>
                      <ListItemText 
                        primary="Timeline" 
                        secondary="How quickly they're looking to implement solutions" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Psychology /></ListItemIcon>
                      <ListItemText 
                        primary="Interests & Pain Points" 
                        secondary="Topics they engage with and potential challenges" 
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Lead Quality Metrics
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Verified Contact Info"
                        secondary="Email and phone validated"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <TrendingUp color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Engagement Score"
                        secondary="Based on social media activity"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <MonetizationOn color="warning" />
                      </ListItemIcon>
                      <ListItemText
                        primary="ROI Potential"
                        secondary="Calculated lead value and conversion probability"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Psychology color="info" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Interest Alignment"
                        secondary="Topics match your services"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={5}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h5" gutterBottom color="primary">
                🏭 Industry-Specific Lead Capture Setup
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Configure your lead capture system for your specific industry to capture the right leads.
              </Typography>
            </Grid>

            {/* Industry Selection */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Industry Configuration
                  </Typography>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Select Your Industry</InputLabel>
                        <Select
                          value={selectedIndustry}
                          onChange={(e) => setSelectedIndustry(e.target.value)}
                          label="Select Your Industry"
                        >
                          <MenuItem value="">
                            <em>Choose an industry...</em>
                          </MenuItem>
                          {Object.entries(industryTemplates).map(([key, template]) => (
                            <MenuItem key={key} value={key}>
                              {template.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {autoDetectedIndustry && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            <strong>Auto-detected:</strong> {industryTemplates[autoDetectedIndustry]?.name}
                          </Typography>
                        </Alert>
                      )}

                      <Button
                        variant="outlined"
                        startIcon={<AutoAwesome />}
                        onClick={() => {
                          // Simulate auto-detection
                          const detected = 'real-estate';
                          setAutoDetectedIndustry(detected);
                          setSelectedIndustry(detected);
                        }}
                        sx={{ mb: 2 }}
                      >
                        Auto-Detect Industry
                      </Button>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      {selectedIndustry && (
                        <Box>
                          <Typography variant="subtitle1" gutterBottom>
                            {industryTemplates[selectedIndustry]?.name} Template
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Pre-configured keywords and scoring for {industryTemplates[selectedIndustry]?.name.toLowerCase()} businesses.
                          </Typography>
                          
                          <Grid container spacing={1}>
                            <Grid item xs={4}>
                              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                                <Typography variant="h6" color="success.dark">
                                  {industryTemplates[selectedIndustry]?.scoring.highIntentScore}
                                </Typography>
                                <Typography variant="caption">High Intent</Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={4}>
                              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
                                <Typography variant="h6" color="warning.dark">
                                  {industryTemplates[selectedIndustry]?.scoring.mediumIntentScore}
                                </Typography>
                                <Typography variant="caption">Medium Intent</Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={4}>
                              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
                                <Typography variant="h6" color="info.dark">
                                  {industryTemplates[selectedIndustry]?.scoring.commentScore}
                                </Typography>
                                <Typography variant="caption">Comments</Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Industry Keywords */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Industry Keywords
                  </Typography>
                  
                  {selectedIndustry ? (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom color="success.main">
                        High Intent Keywords
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        {industryTemplates[selectedIndustry]?.keywords.highIntent.map((keyword, index) => (
                          <Chip
                            key={index}
                            label={keyword}
                            size="small"
                            color="success"
                            sx={{ m: 0.5 }}
                          />
                        ))}
                      </Box>

                      <Typography variant="subtitle2" gutterBottom color="warning.main">
                        Medium Intent Keywords
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        {industryTemplates[selectedIndustry]?.keywords.mediumIntent.map((keyword, index) => (
                          <Chip
                            key={index}
                            label={keyword}
                            size="small"
                            color="warning"
                            sx={{ m: 0.5 }}
                          />
                        ))}
                      </Box>

                      <Typography variant="subtitle2" gutterBottom color="info.main">
                        Low Intent Keywords
                      </Typography>
                      <Box>
                        {industryTemplates[selectedIndustry]?.keywords.lowIntent.map((keyword, index) => (
                          <Chip
                            key={index}
                            label={keyword}
                            size="small"
                            color="info"
                            sx={{ m: 0.5 }}
                          />
                        ))}
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Select an industry to view keywords
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Custom Keywords */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Custom Keywords
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Add specific keywords for your business niche.
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Add custom keyword"
                        placeholder="e.g., luxury homes, emergency repair"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && e.target.value.trim()) {
                            setCustomKeywords([...customKeywords, e.target.value.trim()]);
                            e.target.value = '';
                          }
                        }}
                      />
                    </Grid>
                    
                    {customKeywords.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                          Your Custom Keywords:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {customKeywords.map((keyword, index) => (
                            <Chip
                              key={index}
                              label={keyword}
                              onDelete={() => setCustomKeywords(customKeywords.filter((_, i) => i !== index))}
                              color="primary"
                            />
                          ))}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Lead Capture Settings */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Capture Settings
                  </Typography>
                  
                  <FormControl component="fieldset" sx={{ width: '100%' }}>
                    <FormGroup>
                      <FormControlLabel
                        control={<Switch checked={true} />}
                        label="Enable industry-specific capture"
                      />
                      <FormControlLabel
                        control={<Switch checked={true} />}
                        label="Auto-detect industry from content"
                      />
                      <FormControlLabel
                        control={<Switch checked={true} />}
                        label="Use industry keywords for scoring"
                      />
                      <FormControlLabel
                        control={<Switch checked={false} />}
                        label="Include custom keywords in scoring"
                      />
                    </FormGroup>
                  </FormControl>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Capture Triggers:
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                        <ListItemText primary="Comments with industry keywords" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                        <ListItemText primary="Shares with buying intent" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                        <ListItemText primary="Profile visits after engagement" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                        <ListItemText primary="Multiple interactions with content" />
                      </ListItem>
                    </List>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Industry Examples */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Industry Examples
                  </Typography>
                  
                  <Grid container spacing={2}>
                    {Object.entries(industryTemplates).slice(0, 3).map(([key, template]) => (
                      <Grid item xs={12} md={4} key={key}>
                        <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                          <Typography variant="subtitle1" gutterBottom>
                            {template.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Example keywords: {template.keywords.highIntent.slice(0, 2).join(', ')}
                          </Typography>
                          <Typography variant="caption" color="primary">
                            High intent score: {template.scoring.highIntentScore} points
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

                <TabPanel value={activeTab} index={6}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    📝 How Lead Capture Works
                  </Typography>
                  
                  <Typography variant="h6" gutterBottom color="primary">
                    🔍 Automated Lead Detection
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Our AI system monitors social media platforms for users who engage with our educational content about social media marketing:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><LinkedIn color="primary" /></ListItemIcon>
                      <ListItemText 
                        primary="LinkedIn Engagement" 
                        secondary="Likes, comments, shares on our posts about social media ROI and lead generation" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Instagram color="primary" /></ListItemIcon>
                      <ListItemText 
                        primary="Instagram Interactions" 
                        secondary="Story views, post likes, comments on our social media marketing content" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Facebook color="primary" /></ListItemIcon>
                      <ListItemText 
                        primary="Facebook Page Activity" 
                        secondary="Page likes, post engagement, comments on our business growth content" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Twitter color="primary" /></ListItemIcon>
                      <ListItemText 
                        primary="Twitter Engagement" 
                        secondary="Retweets, likes, replies to our social media marketing posts" 
                      />
                    </ListItem>
                  </List>

                  <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                    📝 How It Works: Content Marketing Funnel
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    We create valuable educational content about social media marketing, and capture leads from people who engage with it:
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>Our Content Strategy:</Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon><Campaign /></ListItemIcon>
                          <ListItemText 
                            primary="Educational Posts" 
                            secondary="Tips about social media ROI, lead generation, analytics" 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><Analytics /></ListItemIcon>
                          <ListItemText 
                            primary="Case Studies" 
                            secondary="Real examples of social media success stories" 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><TrendingUp /></ListItemIcon>
                          <ListItemText 
                            primary="Industry Insights" 
                            secondary="Latest trends in social media marketing" 
                          />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>Lead Capture Process:</Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon><Person /></ListItemIcon>
                          <ListItemText 
                            primary="People engage with our content" 
                            secondary="Likes, comments, shares on educational posts" 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><Business /></ListItemIcon>
                          <ListItemText 
                            primary="AI identifies qualified prospects" 
                            secondary="Based on position, company, engagement patterns" 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><MonetizationOn /></ListItemIcon>
                          <ListItemText 
                            primary="Leads provided to clients" 
                            secondary="Ready-to-contact prospects with full details" 
                          />
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>

                  <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                    🎯 Our Content vs. Client Content
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    We create educational content about social media marketing that attracts potential clients:
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom color="success.main">Our Educational Content:</Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                          <ListItemText 
                            primary="Social Media ROI Tips" 
                            secondary="How to measure and improve social media returns" 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                          <ListItemText 
                            primary="Lead Generation Strategies" 
                            secondary="Best practices for capturing leads from social media" 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                          <ListItemText 
                            primary="Analytics & Reporting" 
                            secondary="How to track and optimize social media performance" 
                          />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom color="info.main">Client Content (Separate):</Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon><Business color="info" /></ListItemIcon>
                          <ListItemText 
                            primary="Their Business Posts" 
                            secondary="Client's own social media content and campaigns" 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><Campaign color="info" /></ListItemIcon>
                          <ListItemText 
                            primary="Brand Marketing" 
                            secondary="Client's product/service promotion and brand building" 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><TrendingUp color="info" /></ListItemIcon>
                          <ListItemText 
                            primary="Engagement Management" 
                            secondary="We help manage and optimize their social media presence" 
                          />
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>

                  <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                    📞 Contact Information Provided
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Each captured lead includes comprehensive contact and business information:
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>Basic Contact Details:</Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon><Person /></ListItemIcon>
                          <ListItemText primary="Full Name (First & Last)" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><Email /></ListItemIcon>
                          <ListItemText primary="Email Address (Verified)" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><Phone /></ListItemIcon>
                          <ListItemText primary="Phone Number (Direct contact)" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><Business /></ListItemIcon>
                          <ListItemText primary="Company & Position (Decision-maker identification)" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><LocationOn /></ListItemIcon>
                          <ListItemText primary="Location (Geographic targeting)" />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>Business Intelligence:</Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon><Work /></ListItemIcon>
                          <ListItemText primary="Industry & Company Size" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><MonetizationOn /></ListItemIcon>
                          <ListItemText primary="Budget Range & Timeline" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><Psychology /></ListItemIcon>
                          <ListItemText primary="Interests & Pain Points" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><TrendingUp /></ListItemIcon>
                          <ListItemText primary="Engagement Patterns" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><Assessment /></ListItemIcon>
                          <ListItemText primary="Lead Score & Qualification" />
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Card>

      {/* Add Lead Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Lead</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                value={leadForm.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={leadForm.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={leadForm.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                value={leadForm.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Company"
                value={leadForm.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Position"
                value={leadForm.position}
                onChange={(e) => handleInputChange('position', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Industry</InputLabel>
                <Select
                  value={leadForm.industry}
                  onChange={(e) => handleInputChange('industry', e.target.value)}
                >
                  <MenuItem value="technology">Technology</MenuItem>
                  <MenuItem value="healthcare">Healthcare</MenuItem>
                  <MenuItem value="finance">Finance</MenuItem>
                  <MenuItem value="education">Education</MenuItem>
                  <MenuItem value="retail">Retail</MenuItem>
                  <MenuItem value="marketing">Marketing</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Source</InputLabel>
                <Select
                  value={leadForm.source}
                  onChange={(e) => handleInputChange('source', e.target.value)}
                >
                  <MenuItem value="linkedin">LinkedIn</MenuItem>
                  <MenuItem value="instagram">Instagram</MenuItem>
                  <MenuItem value="facebook">Facebook</MenuItem>
                  <MenuItem value="website">Website</MenuItem>
                  <MenuItem value="referral">Referral</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Budget Range"
                value={leadForm.budget}
                onChange={(e) => handleInputChange('budget', e.target.value)}
                placeholder="e.g., $5K-$10K"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Timeline"
                value={leadForm.timeline}
                onChange={(e) => handleInputChange('timeline', e.target.value)}
                placeholder="e.g., 1-3 months"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={leadForm.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Additional notes about the lead..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Adding...' : 'Add Lead'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lead Detail Dialog */}
      <Dialog 
        open={!!selectedLead} 
        onClose={() => setSelectedLead(null)} 
        maxWidth="md" 
        fullWidth
      >
        {selectedLead && (
          <>
            <DialogTitle>
              <Typography variant="h6">
                Lead Details: {selectedLead.firstName} {selectedLead.lastName}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Contact Information</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><Email /></ListItemIcon>
                      <ListItemText primary={selectedLead.email} />
                    </ListItem>
                    {selectedLead.phone && (
                      <ListItem>
                        <ListItemIcon><Phone /></ListItemIcon>
                        <ListItemText primary={selectedLead.phone} />
                      </ListItem>
                    )}
                    <ListItem>
                      <ListItemIcon><Business /></ListItemIcon>
                      <ListItemText primary={`${selectedLead.position} at ${selectedLead.company}`} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><LocationOn /></ListItemIcon>
                      <ListItemText primary={selectedLead.location} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Work /></ListItemIcon>
                      <ListItemText primary={selectedLead.industry} />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>ROI Metrics</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><MonetizationOn /></ListItemIcon>
                      <ListItemText 
                        primary={`$${selectedLead.value.toLocaleString()}`} 
                        secondary="Lead Value" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><TrendingUp /></ListItemIcon>
                      <ListItemText 
                        primary={`${selectedLead.roi.roi}%`} 
                        secondary="ROI" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Psychology /></ListItemIcon>
                      <ListItemText 
                        primary={`${(selectedLead.conversionProbability * 100).toFixed(0)}%`} 
                        secondary="Conversion Probability" 
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Capture Details</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><Campaign /></ListItemIcon>
                      <ListItemText 
                        primary="Capture Method" 
                        secondary={selectedLead.captureMethod || 'Manual Entry'} 
                      />
                    </ListItem>
                    {selectedLead.captureDetails && (
                      <ListItem>
                        <ListItemIcon><Analytics /></ListItemIcon>
                        <ListItemText 
                          primary="Capture Details" 
                          secondary={selectedLead.captureDetails} 
                        />
                      </ListItem>
                    )}
                    <ListItem>
                      <ListItemIcon><TrendingUp /></ListItemIcon>
                      <ListItemText 
                        primary="Engagement Score" 
                        secondary={`${selectedLead.engagementScore}/100`} 
                      />
                    </ListItem>
                  </List>
                </Grid>

                {selectedLead.contactInfo && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>Additional Contact Info</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <List dense>
                          {selectedLead.contactInfo.linkedin && (
                            <ListItem>
                              <ListItemIcon><LinkedIn /></ListItemIcon>
                              <ListItemText 
                                primary="LinkedIn" 
                                secondary={selectedLead.contactInfo.linkedin} 
                              />
                            </ListItem>
                          )}
                          {selectedLead.contactInfo.twitter && (
                            <ListItem>
                              <ListItemIcon><Twitter /></ListItemIcon>
                              <ListItemText 
                                primary="Twitter" 
                                secondary={selectedLead.contactInfo.twitter} 
                              />
                            </ListItem>
                          )}
                          {selectedLead.contactInfo.instagram && (
                            <ListItem>
                              <ListItemIcon><Instagram /></ListItemIcon>
                              <ListItemText 
                                primary="Instagram" 
                                secondary={selectedLead.contactInfo.instagram} 
                              />
                            </ListItem>
                          )}
                          {selectedLead.contactInfo.facebook && (
                            <ListItem>
                              <ListItemIcon><Facebook /></ListItemIcon>
                              <ListItemText 
                                primary="Facebook" 
                                secondary={selectedLead.contactInfo.facebook} 
                              />
                            </ListItem>
                          )}
                        </List>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <List dense>
                          {selectedLead.contactInfo.companyWebsite && (
                            <ListItem>
                              <ListItemIcon><Business /></ListItemIcon>
                              <ListItemText 
                                primary="Company Website" 
                                secondary={selectedLead.contactInfo.companyWebsite} 
                              />
                            </ListItem>
                          )}
                          {selectedLead.contactInfo.companySize && (
                            <ListItem>
                              <ListItemIcon><People /></ListItemIcon>
                              <ListItemText 
                                primary="Company Size" 
                                secondary={selectedLead.contactInfo.companySize} 
                              />
                            </ListItem>
                          )}
                          {selectedLead.contactInfo.budget && (
                            <ListItem>
                              <ListItemIcon><MonetizationOn /></ListItemIcon>
                              <ListItemText 
                                primary="Budget Range" 
                                secondary={selectedLead.contactInfo.budget} 
                              />
                            </ListItem>
                          )}
                          {selectedLead.contactInfo.timeline && (
                            <ListItem>
                              <ListItemIcon><Schedule /></ListItemIcon>
                              <ListItemText 
                                primary="Timeline" 
                                secondary={selectedLead.contactInfo.timeline} 
                              />
                            </ListItem>
                          )}
                        </List>
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                {selectedLead.interests && selectedLead.interests.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>Interests & Topics</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedLead.interests.map((interest, index) => (
                        <Chip key={index} label={interest} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Grid>
                )}

                {selectedLead.qualificationCriteria && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>Qualification Criteria</Typography>
                    <List dense>
                      {selectedLead.qualificationCriteria.map((criteria, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <CheckCircle color="success" />
                          </ListItemIcon>
                          <ListItemText primary={criteria} />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                )}

                {selectedLead.captureTriggers && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>Capture Triggers</Typography>
                    <List dense>
                      {selectedLead.captureTriggers.map((trigger, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <Campaign color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={trigger} />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                )}

                {selectedLead.notes && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>Notes</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedLead.notes}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Social Activity</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="primary">
                          {selectedLead.socialActivity.posts}
                        </Typography>
                        <Typography variant="body2">Posts</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="primary">
                          {selectedLead.socialActivity.engagement}
                        </Typography>
                        <Typography variant="body2">Engagement</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="primary">
                          {selectedLead.socialActivity.reach.toLocaleString()}
                        </Typography>
                        <Typography variant="body2">Reach</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedLead(null)}>Close</Button>
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => handleContactLead(selectedLead)}
              >
                Contact Lead
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Contact Lead Dialog */}
      <Dialog open={showContactDialog} onClose={() => setShowContactDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Email color="primary" />
            Contact {selectedLead?.firstName} {selectedLead?.lastName}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            {/* Contact Method Selection */}
            <Grid item xs={12}>
              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Contact Method:</Typography>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={contactMethod === 'email'}
                        onChange={() => setContactMethod('email')}
                      />
                    }
                    label="Email"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={contactMethod === 'text'}
                        onChange={() => setContactMethod('text')}
                      />
                    }
                    label="Text Message"
                  />
                </FormGroup>
              </FormControl>
            </Grid>

            {/* Lead Contact Info */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Lead Contact Info</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><Person /></ListItemIcon>
                      <ListItemText 
                        primary={`${selectedLead?.firstName} ${selectedLead?.lastName}`}
                        secondary="Full Name"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Email /></ListItemIcon>
                      <ListItemText 
                        primary={selectedLead?.email}
                        secondary="Email Address"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Phone /></ListItemIcon>
                      <ListItemText 
                        primary={selectedLead?.phone}
                        secondary="Phone Number"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Business /></ListItemIcon>
                      <ListItemText 
                        primary={selectedLead?.company}
                        secondary="Company"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Template Selection */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Message Template</Typography>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Select Template</InputLabel>
                    <Select
                      value={contactTemplate}
                      onChange={(e) => {
                        setContactTemplate(e.target.value);
                        const template = contactTemplates[selectedIndustry] || contactTemplates['real-estate'];
                        if (e.target.value === 'email') {
                          setContactSubject(template.email.subject);
                          setContactMessage(template.email.message);
                        } else {
                          setContactMessage(template.text.message);
                        }
                      }}
                      label="Select Template"
                    >
                      <MenuItem value="email">Email Template</MenuItem>
                      <MenuItem value="text">Text Template</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <Typography variant="body2" color="text.secondary">
                    Template includes industry-specific messaging for {industryTemplates[selectedIndustry]?.name || 'your industry'}.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Message Content */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Message Content</Typography>
                  
                  {contactMethod === 'email' && (
                    <TextField
                      fullWidth
                      label="Subject"
                      value={contactSubject}
                      onChange={(e) => setContactSubject(e.target.value)}
                      sx={{ mb: 2 }}
                    />
                  )}
                  
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    label="Message"
                    value={contactMessage}
                    onChange={(e) => setContactMessage(e.target.value)}
                    helperText="Use {{firstName}}, {{clientName}}, {{clientCompany}} for personalization"
                  />
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Preview:</Typography>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                        {replaceTemplateVariables(contactMessage, selectedLead || {})}
                      </Typography>
                    </Paper>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Integration Status */}
            <Grid item xs={12}>
              <Alert severity="warning">
                <Typography variant="body2">
                  <strong>⚠️ Integration Required:</strong> You need to connect your email and SMS services to send messages. 
                  <Button 
                    size="small" 
                    color="primary" 
                    onClick={() => setShowIntegrationDialog(true)}
                    sx={{ ml: 1 }}
                  >
                    Setup Integrations
                  </Button>
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowContactDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={sendContact}
            disabled={sendingContact}
            startIcon={sendingContact ? <CircularProgress size={20} /> : <Send />}
          >
            {sendingContact ? 'Sending...' : `Send ${contactMethod === 'email' ? 'Email' : 'Text'}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Integration Setup Dialog */}
      <Dialog open={showIntegrationDialog} onClose={() => setShowIntegrationDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Settings color="primary" />
            Connect Your Email & SMS Services
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Connect your email and SMS services to send messages directly from the platform.
          </Typography>

          <Grid container spacing={3}>
            {/* Email Integration */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📧 Email Integration
                  </Typography>
                  
                  {integrationStatus.email.connected ? (
                    <Box>
                      <Alert severity="success" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Connected:</strong> {integrationStatus.email.provider} ({integrationStatus.email.email})
                        </Typography>
                      </Alert>
                      <Button 
                        variant="outlined" 
                        color="error" 
                        onClick={() => disconnectService('email')}
                        size="small"
                      >
                        Disconnect
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Connect your email account to send professional emails to leads.
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => connectEmailService('Gmail')}
                            startIcon={<Email />}
                            size="small"
                          >
                            Gmail
                          </Button>
                        </Grid>
                        <Grid item xs={6}>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => connectEmailService('Outlook')}
                            startIcon={<Email />}
                            size="small"
                          >
                            Outlook
                          </Button>
                        </Grid>
                        <Grid item xs={6}>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => connectEmailService('SMTP')}
                            startIcon={<Email />}
                            size="small"
                          >
                            SMTP
                          </Button>
                        </Grid>
                        <Grid item xs={6}>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => connectEmailService('Custom')}
                            startIcon={<Email />}
                            size="small"
                          >
                            Custom
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* SMS Integration */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📱 SMS Integration
                  </Typography>
                  
                  {integrationStatus.sms.connected ? (
                    <Box>
                      <Alert severity="success" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Connected:</strong> {integrationStatus.sms.provider} ({integrationStatus.sms.phone})
                        </Typography>
                      </Alert>
                      <Button 
                        variant="outlined" 
                        color="error" 
                        onClick={() => disconnectService('sms')}
                        size="small"
                      >
                        Disconnect
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Connect your SMS service to send text messages to leads.
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => connectSMSService('Twilio')}
                            startIcon={<Phone />}
                            size="small"
                          >
                            Twilio
                          </Button>
                        </Grid>
                        <Grid item xs={6}>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => connectSMSService('MessageBird')}
                            startIcon={<Phone />}
                            size="small"
                          >
                            MessageBird
                          </Button>
                        </Grid>
                        <Grid item xs={6}>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => connectSMSService('Vonage')}
                            startIcon={<Phone />}
                            size="small"
                          >
                            Vonage
                          </Button>
                        </Grid>
                        <Grid item xs={6}>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => connectSMSService('Custom')}
                            startIcon={<Phone />}
                            size="small"
                          >
                            Custom
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Integration Instructions */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    🔧 How It Works
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>Email Integration:</Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                          <ListItemText primary="OAuth authentication with your email provider" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                          <ListItemText primary="Sends emails from your business email address" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                          <ListItemText primary="Tracks opens, clicks, and responses" />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>SMS Integration:</Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                          <ListItemText primary="API connection to your SMS service" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                          <ListItemText primary="Sends texts from your business phone number" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                          <ListItemText primary="Tracks delivery status and responses" />
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Security & Privacy */}
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>🔒 Security:</strong> We use industry-standard OAuth and API connections. Your credentials are encrypted and never stored in plain text. You can revoke access at any time.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowIntegrationDialog(false)}>Close</Button>
          <Button 
            variant="contained" 
            color="primary"
            disabled={!integrationStatus.email.connected && !integrationStatus.sms.connected}
          >
            Save Integrations
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadCapture; 